import {
    map, Observable, throwError
} from "rxjs";

import {
    Injectable, inject
} from "@angular/core";
import {
    ProductBatch,
    ProductBatchApproveDealsOptions,
    ProductBatchOperation
} from "@shopliftr/common-js/admin";
import { ModelAction } from "@shopliftr/common-js/data";
import {
    BulkResponse,
    BulkUtil,
    CustomOpResponse,
    GetResponse,
    RequestUtil
} from "@shopliftr/common-js/shared";

import { InvalidParametersException } from "../../../../exceptions/invalid-parameters-exception";
import { IReadOnlyInternalApiClientService } from "../../../../interfaces/internal-api-client";
import { InternalApiRequestService } from "../../request/internal-api/internal-api-request.service";

/**
 * Leverages the ProductBatch endpoint to retrieve product batches and approve their deals.
 * Implements IReadOnlyInternalApiClientService because the Internal API v2 `product-batch` endpoint
 * only supports GET and the `APPROVE_DEALS` custom operation; there is no search, create, update or delete.
 *
 * @pattern IReadOnlyInternalApiClientService
 * @providedIn root
 */
@Injectable({ providedIn: "root" })
export class ProductBatchApiClientService implements IReadOnlyInternalApiClientService<ProductBatch> {
    readonly basePath = "product-batch";

    private readonly _internalApiRequestService = inject(InternalApiRequestService);

    /**
     * Retrieves multiple product batches by their IDs in a single bulk request.
     *
     * @param ids - Array of product batch IDs to retrieve
     * @returns Observable of bulk response containing the requested product batches
     * @see InternalApiRequestService.bulk$
     */
    bulkGet$(ids: Array<string>): Observable<BulkResponse> {
        const bulkRequest = BulkUtil.createBulkRequest(ids.map((id: string) => {
            const request = RequestUtil.createGetRequest<ProductBatch>(id, ProductBatch.modelName, ModelAction.ClientGet);
            return BulkUtil.createRequestItem(request);
        }));
        return this._internalApiRequestService.bulk$(bulkRequest);
    }

    /**
     * Retrieves a single product batch by ID.
     *
     * @param id - ID of the product batch to retrieve
     * @returns Observable of the product batch
     * @see InternalApiRequestService.get$
     */
    get$(id: string): Observable<ProductBatch> {

        const request = RequestUtil.createGetRequest<void>(id, ProductBatch.modelName, ModelAction.ClientGet);

        return this._internalApiRequestService.get$<ProductBatch>(request, this.basePath)
            .pipe(map((response: GetResponse<ProductBatch>) => response.payload));
    }

    /**
     * Approves the deals of a product batch by issuing the `APPROVE_DEALS` custom operation, which stamps the
     * given flyer dates on the batch's deals metadata.
     *
     * @param id - ID of the product batch whose deals are approved
     * @param flyerStartDate - Flyer start date; must be at the start of the day
     * @param flyerEndDate - Flyer end date; must be at the end of the day
     * @returns Observable of the updated product batch. Errors with InvalidParametersException when any argument is missing
     * @see InternalApiRequestService.customOp$
     */
    approveDeals$(id: string, flyerStartDate: Date, flyerEndDate: Date): Observable<ProductBatch> {

        if (!id || !flyerStartDate || !flyerEndDate) {
            return throwError(() => new InvalidParametersException(
                "Missing one or more required fields: id, flyerStartDate, flyerEndDate"
            ));
        }

        const options = new ProductBatchApproveDealsOptions();
        options.flyerStartDate = flyerStartDate;
        options.flyerEndDate = flyerEndDate;

        const request = RequestUtil.createCustomOpRequest<ProductBatchApproveDealsOptions>(
            id,
            ProductBatch.modelName,
            ProductBatchOperation.ApproveDeals,
            options
        );

        return this._internalApiRequestService.customOp$<ProductBatch, ProductBatchApproveDealsOptions>(request, this.basePath).pipe(
            map((response: CustomOpResponse<ProductBatch>) => response.payload)
        );
    }
}
